
import { flushSync } from "react-dom";
import { Message } from "../message-item";
import { createUserMessage, createAssistantMessage, createSystemMessage } from "../constants";
import { AssistantResponse, callAssistant } from "../api/assistant-api";
import { nanoid } from "nanoid";
import { ConversationMessageService } from "../services/conversation-message-service";
import { supabase } from "@/integrations/supabase/client";

// Import the new intent-based system
import { intentMatcher } from "../api/intents";
import { 
  shouldSearchDocuments, 
  searchDocumentsWithFallback, 
  formatDocumentSearchResults
} from "../api/services/integrated-semantic-search";

// Import guidance service for fallback
import {
  generateGuidanceResponse
} from "../api/services/guidance-service";

// Import database API for integrations
import { callDatabaseApi } from "../api/database-api";

// Import AI-enhanced job search
import { searchJobsWithAI } from "../api/services/ai-enhanced-job-search";

// Import AI-enhanced education search
import { searchEducationWithAI } from "../api/services/ai-enhanced-education-search";

// Import conversation persistence utilities
import { saveConversationToDatabase, testDatabaseSaving } from "../utils/conversation-db";

interface UseChatMessageSenderProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setApiError: React.Dispatch<React.SetStateAction<string | null>>;
  setHasPrivacyWarning: React.Dispatch<React.SetStateAction<boolean>>;
  setIsProcessingLargeDataset: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  isProcessingLargeDataset: boolean;
  threadId: string | null;
  onThreadIdChange: (threadId: string | null) => void;
  toast: any;
}

/**
 * Enhanced saving with retry logic
 */
const saveConversationWithRetry = async (
  threadId: string, 
  messages: Message[], 
  maxRetries: number = 3
): Promise<boolean> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    console.log(`💾 [RETRY-SAVE] Attempt ${attempt}/${maxRetries} for thread: ${threadId}`);
    
    const success = await saveConversationToDatabase(threadId, messages);
    if (success) {
      console.log(`✅ [RETRY-SAVE] Succeeded on attempt ${attempt}`);
      return true;
    }
    
    if (attempt < maxRetries) {
      console.log(`⚠️ [RETRY-SAVE] Attempt ${attempt} failed, retrying in ${attempt * 1000}ms...`);
      await new Promise(resolve => setTimeout(resolve, attempt * 1000));
    }
  }
  
  console.error(`❌ [RETRY-SAVE] All ${maxRetries} attempts failed for thread: ${threadId}`);
  return false;
};


// TODO: Refactor this to use a more structured approach with better visibility and splitting
// into smaller functions. In particular, we should handle the cases where we do not call OpenAI
// but instead return data from APIs or documents directly much better. It is currently interleaved
// with the OpenAI call path and this makes it hard to follow the logic.
export const useChatMessageSender = ({
  messages,
  setMessages,
  setIsLoading,
  setApiError,
  setHasPrivacyWarning,
  setIsProcessingLargeDataset,
  isLoading,
  isProcessingLargeDataset,
  threadId,
  onThreadIdChange,
  toast
}: UseChatMessageSenderProps) => {
  
  const sendMessage = async (content: string) => {
    if (isLoading || isProcessingLargeDataset) return;
    // We default to calling OpenAI unless we have API context or document context
    let shouldCallOpenAI: boolean = true;
    
    console.log('🚀 [CHAT-SENDER] ===== NEW MESSAGE SEND PROCESS =====');
    console.log('🚀 [CHAT-SENDER] Message content:', content);
    console.log('🔗 [CHAT-SENDER] Current threadId:', threadId);
    console.log('🔗 [CHAT-SENDER] Current messages count:', messages.length);
    
    // CRITICAL: Debug authentication before sending
    console.log('🔧 [CHAT-SENDER] Checking authentication before sending message...');
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('❌ [CHAT-SENDER] Session error detected:', sessionError);
        toast({
          title: "Autentiseringsfeil",
          description: "Kunne ikke verifisere innlogging. Prøv å logg inn på nytt.",
          variant: "destructive",
        });
        return;
      }
      
      if (!session) {
        console.error('❌ [CHAT-SENDER] No session found');
        toast({
          title: "Ikke logget inn",
          description: "Du må logge inn for å sende meldinger.",
          variant: "destructive",
        });
        return;
      }
      
      console.log('✅ [CHAT-SENDER] Authentication verified:', {
        userId: session.user?.id,
        email: session.user?.email,
        hasAccessToken: !!session.access_token,
        expiresAt: session.expires_at
      });
    } catch (authError) {
      console.error('❌ [CHAT-SENDER] Authentication check failed:', authError);
      toast({
        title: "Autentiseringsproblem",
        description: "Kunne ikke verifisere innlogging. Prøv å last siden på nytt.",
        variant: "destructive",
      });
      return;
    }
    
    const userMessage = createUserMessage(content);
    // updatedMessages is the current state of messages with the new user message added.
    // This is what is saved to the database and displayed in the UI, not necessarily what
    // is sent to the OpenAI API.
    const updatedMessages = [...messages, userMessage];
    
    // CRITICAL FIX: Force immediate rendering of user message
    flushSync(() => {
      setMessages(updatedMessages);
    });
    
    // Then set loading state and continue processing
    setIsLoading(true);
    setApiError(null);
    
    try {
      // PRIORITY 1: Use new intent-based system for query analysis
      console.log('🎯 [CHAT-SENDER] Using intent-based query detection...');
      const intentResult = intentMatcher.matchIntent(content);
      
      // Check if guidance should be provided
      if ('shouldProvideGuidance' in intentResult && intentResult.shouldProvideGuidance) {
        console.log('🤔 [CHAT-SENDER] Intent system suggests guidance:', intentResult.guidanceType);
        
        // Generate guidance response using existing service
        const guidanceResponse = generateGuidanceResponse(content);
        if (guidanceResponse) {
          console.log('✅ [CHAT-SENDER] Providing guidance response:', guidanceResponse.guidanceType);
          
          const assistantMessage = createAssistantMessage(guidanceResponse.formattedResponse);
          const finalMessages = [...updatedMessages, assistantMessage];
          setMessages(finalMessages);
          
          // Generate thread ID if we don't have one
          let currentThreadId = threadId;
          if (!currentThreadId) {
            currentThreadId = `thread_${nanoid()}`;
            console.log('🧵 [CHAT-SENDER] Generated new thread ID:', currentThreadId);
            onThreadIdChange(currentThreadId);
            localStorage.setItem('klara_thread_id', currentThreadId);
          }
          
          // Save messages to database if we have a thread
          if (currentThreadId) {
            console.log('💾 [CHAT-SENDER] Saving guidance conversation to database');
            await ConversationMessageService.saveMessages(currentThreadId, finalMessages);
            await saveConversationWithRetry(currentThreadId, finalMessages);
          }
          
          setIsLoading(false);
          return; // Don't call OpenAI or make API calls
        }
      }
      
      console.log('✅ [CHAT-SENDER] Intent analysis complete:', {
        intent: 'intent' in intentResult ? intentResult.intent : 'guidance',
        handler: 'handler' in intentResult ? intentResult.handler : 'none',
        confidence: 'confidence' in intentResult ? intentResult.confidence : 0
      });
      
      // PRIORITY 2: ENHANCED API integrations
      let apiContext = '';
      if ('handler' in intentResult && intentResult.confidence > 0.4) {
        const handler = intentResult.handler;
        
        if (handler === 'brreg') {
          console.log('🔌 [CHAT-SENDER] Intent-based API call triggered for handler:', handler);
          
          try {
            const apiResponse = await callDatabaseApi({
              query: content,
              database: handler,
              maxResults: 10
            });
            
            const isValidResponse = apiResponse && 
                                  apiResponse.formattedResponse && 
                                  !apiResponse.error &&
                                  (!apiResponse.result?.metadata?.antiHallucinationCheck || 
                                   apiResponse.result?.metadata?.searchSuccessful);
            
            console.log('🔍 [CHAT-SENDER] API response validation:', {
              hasResponse: !!apiResponse,
              hasFormattedResponse: !!apiResponse?.formattedResponse,
              hasError: !!apiResponse?.error,
              isValid: isValidResponse
            });
            
            if (isValidResponse) {
              const responseText = apiResponse.formattedResponse.toLowerCase();
              const hasMockIndicators = [
                'mock data',
                'testdata', 
                'mock-data',
                'ikke reelle',
                'ikke ekte',
                'api-kallet feilet',
                'fallback-data',
                'anti-hallusinasjon'
              ].some(indicator => responseText.includes(indicator));
              
              if (hasMockIndicators) {
                console.warn('⚠️ [CHAT-SENDER] Mock data indicators detected in API response');
                
                const errorMessage = createAssistantMessage(
                  `Jeg kan dessverre ikke hente ekte data for søket "${content}" akkurat nå. ` +
                  `Systemet har blokkert potensielt uekte resultater for å forhindre feilinformasjon.\n\n` +
                  `**For å få ekte resultater:**\n` +
                  `- Gå direkte til [Enhetsregisteret](https://www.brreg.no/bedriftssok/)\n` +
                  `- Søk manuelt etter "${content}"\n\n` +
                  `*🛡️ Anti-hallusinasjonssystem: Beskytter mot uekte data*`
                );
                
                const finalMessages = [...updatedMessages, errorMessage];
                setMessages(finalMessages);
                
                // Generate thread ID if we don't have one
                let currentThreadId = threadId;
                if (!currentThreadId) {
                  currentThreadId = `thread_${nanoid()}`;
                  console.log('🧵 [CHAT-SENDER] Generated new thread ID:', currentThreadId);
                  onThreadIdChange(currentThreadId);
                  localStorage.setItem('klara_thread_id', currentThreadId);
                }
                
                if (currentThreadId) {
                  await ConversationMessageService.saveMessages(currentThreadId, finalMessages);
                  await saveConversationWithRetry(currentThreadId, finalMessages);
                }
                
                setIsLoading(false);
                return;
              }
              
              apiContext = apiResponse.formattedResponse;
              
              console.log('✅ [CHAT-SENDER] Verified API integration successful');
            }
          } catch (error) {
            console.warn('⚠️ [CHAT-SENDER] API integration call failed:', error);
          }
        }
        
        else if (handler === 'arbeidsplassen' && intentResult.confidence > 0.9) {
          console.log('💼 [CHAT-SENDER] AI-enhanced job search triggered');
          
          try {
            const aiJobResult = await searchJobsWithAI(content, 15);
            
            if (aiJobResult.success && aiJobResult.results.length > 0) {
              console.log('✅ [CHAT-SENDER] AI-enhanced Arbeidsplassen.no search successful');
              
              apiContext = aiJobResult.formattedResponse;
              
            } else {
              console.log('⚠️ [CHAT-SENDER] AI-enhanced search failed, falling back');
              
              const fallbackResponse = await callDatabaseApi({
                query: content,
                database: 'arbeidsplassen',
                maxResults: 15
              });
              
              if (fallbackResponse && fallbackResponse.formattedResponse) {
                apiContext = fallbackResponse.formattedResponse;
              }
            }
          } catch (error) {
            console.warn('⚠️ [CHAT-SENDER] AI-enhanced job search failed:', error);
          }
        }
        
        else if (handler === 'utdanning' && intentResult.confidence > 0.9) {
          console.log('🎓 [CHAT-SENDER] AI-enhanced education search triggered');
          
          try {
            const aiEducationResult = await searchEducationWithAI(content, 10);
            
            if (aiEducationResult && aiEducationResult.success && aiEducationResult.results.length > 0) {
              console.log('✅ [CHAT-SENDER] AI-enhanced Utdanning.no search successful');
              
              apiContext = aiEducationResult.formattedResponse;
              
            } else {
              console.log('⚠️ [CHAT-SENDER] AI-enhanced search failed, falling back');
              
              const fallbackResponse = await callDatabaseApi({
                query: content,
                database: 'utdanning',
                maxResults: 10
              });
              
              if (fallbackResponse && fallbackResponse.formattedResponse) {
                apiContext = fallbackResponse.formattedResponse;
              }
            }
          } catch (error) {
            console.warn('⚠️ [CHAT-SENDER] AI-enhanced education search failed:', error);
          }
        }
      }
      
      // PRIORITY 3: Check document search
      let documentContext = '';
      console.log('🔍 [CHAT-SENDER] Checking if document search should be triggered...');
      const shouldSearch = shouldSearchDocuments(content);
      
      if (shouldSearch) {
        console.log('📚 [CHAT-SENDER] Document search triggered for query:', content);
        
        try {
          const searchResult = await searchDocumentsWithFallback(content);
          if (searchResult && searchResult.success && searchResult.results.length > 0) {
            documentContext = formatDocumentSearchResults(searchResult);
            console.log('✅ [CHAT-SENDER] Document search successful, found:', searchResult.results.length, 'documents');
          }
        } catch (error) {
          console.warn('⚠️ [CHAT-SENDER] Document search failed:', error);
        }
      }
      
      // PRIORITY 4: Prepare messages for OpenAI - ALWAYS include system instructions
      // aiPromptMessages is the array of messages that will be sent to OpenAI. This is not
      // necessarily exactly what is displayed in the UI (notably, we include system instructions here).
      let aiPromptMessages: Message[] = [];
      
      // CRITICAL FIX: Always include system instructions as first message
      console.log('🔧 [CHAT-SENDER] Adding system instructions as first message');
      const systemInstructionsMessage = createSystemMessage('SYSTEM_INSTRUCTIONS');
      
      aiPromptMessages.push(systemInstructionsMessage);
      aiPromptMessages = [...aiPromptMessages, ...messages];
      
      if (apiContext) {
        console.log('🔌 [CHAT-SENDER] Adding VERIFIED API context as ASSISTANT message');

        const apiAssistantMessage = createAssistantMessage(apiContext);
        aiPromptMessages.push(userMessage);
        aiPromptMessages.push(apiAssistantMessage);
        shouldCallOpenAI = false; // Skip OpenAI if we have API context
      }
      else if (documentContext) {
        console.log('📤 [CHAT-SENDER] Adding document context as ASSISTANT message');

        const documentContent = documentContext;
        const documentAssistantMessage = createAssistantMessage(documentContent);
        aiPromptMessages.push(userMessage);
        aiPromptMessages.push(documentAssistantMessage);
        shouldCallOpenAI = false; // Skip OpenAI if we have document context
      }
      
      console.log('📤 [CHAT-SENDER] Calling assistant API with', aiPromptMessages.length, 'messages');
      console.log('🔍 [CHAT-SENDER] Has system instructions:', aiPromptMessages.some(m => m.content === 'SYSTEM_INSTRUCTIONS'));
      console.log('🔍 [CHAT-SENDER] Has API context:', !!apiContext);
      console.log('🔍 [CHAT-SENDER] Has document context:', !!documentContext);
      console.log('🔍 [CHAT-SENDER] Should call OpenAI:', shouldCallOpenAI);

      let response: AssistantResponse | null = null;

      // If we *don't* have hard data from APIs or documents, we call OpenAI.
      // In the case we *do* have that data, we don't call OpenAI and instead just return our data.
      // This avoids the model hallucinating or otherwise mangling good data.
      // Do note that it is up to us to ensure proper formatting of the data we get from the APIs
      // and documents before sending it to the user.
      if (shouldCallOpenAI) {
        console.log('🔌 [CHAT-SENDER] Calling OpenAI assistant API...');
        aiPromptMessages = [...aiPromptMessages, userMessage];
        response = await callAssistant(content, threadId, aiPromptMessages);
      } else {
        console.log('🔌 [CHAT-SENDER] Skipping OpenAI API call');
      }
      
      // CRITICAL: Handle thread ID immediately when received
      let currentThreadId = threadId;
      if (response && response.threadId && response.threadId !== threadId) {
        console.log('💾 [CHAT-SENDER] ===== NEW THREAD ID RECEIVED =====');
        console.log('💾 [CHAT-SENDER] Old thread ID:', threadId);
        console.log('💾 [CHAT-SENDER] New thread ID:', response.threadId);
        
        currentThreadId = response.threadId;
        onThreadIdChange(response.threadId);
        localStorage.setItem('klara_thread_id', response.threadId);
        
        // IMMEDIATE TEST AND SAVE: Test database connectivity and save conversation metadata
        console.log('🔄 [CHAT-SENDER] Immediately testing database and saving conversation with new thread ID');
        
        // First test database connectivity
        const dbTest = await testDatabaseSaving();
        if (!dbTest) {
          console.error('❌ [CHAT-SENDER] Database test failed with new thread ID');
          toast({
            title: "Database Problem",
            description: "Kan ikke koble til database. Samtalen lagres ikke.",
            variant: "destructive",
          });
        } else {
          console.log('✅ [CHAT-SENDER] Database test passed with new thread ID');
          
          try {
            const initialSaveResult = await saveConversationWithRetry(currentThreadId, updatedMessages);
            if (initialSaveResult) {
              console.log('✅ [CHAT-SENDER] Initial conversation save successful');
              toast({
                title: "Samtale opprettet",
                description: "Ny samtale er lagret og vil vises under 'Tidligere samtaler'.",
              });
            } else {
              console.error('❌ [CHAT-SENDER] Initial conversation save failed');
              toast({
                title: "Lagringsfeil",
                description: "Kunne ikke lagre samtalen til database.",
                variant: "destructive",
              });
            }
          } catch (error) {
            console.error('❌ [CHAT-SENDER] Initial conversation save exception:', error);
          }
        }
      } else if (!currentThreadId) {
        // If we still don't have a thread ID, generate one
        currentThreadId = `thread_${nanoid()}`;
        console.log('🧵 [CHAT-SENDER] Generated thread ID since none was received:', currentThreadId);
        onThreadIdChange(currentThreadId);
        localStorage.setItem('klara_thread_id', currentThreadId);
        
        toast({
          title: "Ny samtale opprettet",
          description: "Samtale-ID generert og vil lagres lokalt.",
        });
      } else {
        console.log('💾 [CHAT-SENDER] Using existing thread ID:', currentThreadId);
      }
      
      // Check for special response properties
      if (response && typeof response === 'object' && 'hasPrivacyWarning' in response && response.hasPrivacyWarning) {
        setHasPrivacyWarning(true);
      }
      
      if (response && typeof response === 'object' && 'isProcessingLargeDataset' in response && response.isProcessingLargeDataset) {
        setIsProcessingLargeDataset(true);
      }
      
      // messagesToSave is the final set of messages that will be saved to the database. 
      let messagesToSave: Message[] = [...updatedMessages];
      if (shouldCallOpenAI && response && response.response) {
        console.log('📤 [CHAT-SENDER] Adding OpenAI response to thread:', response.response);
        const assistantMessage = createAssistantMessage(response.response);
        messagesToSave = [...messagesToSave, assistantMessage];
      } else {
        console.log('📤 [CHAT-SENDER] No OpenAI response, adding API or document context');
        // If we didn't call OpenAI, we should have added the API or document context already
        if (apiContext) {
          console.log('📤 [CHAT-SENDER] Adding API context to messages:', apiContext);
          messagesToSave.push(createAssistantMessage(apiContext));
        } else if (documentContext) {
          console.log('📤 [CHAT-SENDER] Adding document context to messages:', documentContext);
          messagesToSave.push(createAssistantMessage(documentContext));
        }
      }
      setMessages(messagesToSave);
      
      // ENHANCED SAVE: Save both messages and conversation metadata with current thread ID
      if (currentThreadId) {
        console.log('💾 [CHAT-SENDER] ===== FINAL SAVE PROCESS =====');
        console.log('💾 [CHAT-SENDER] Final thread ID:', currentThreadId);
        console.log('💾 [CHAT-SENDER] Final messages count:', messagesToSave.length);
        
        try {
          // First create our conversation
          console.log('💾 [CHAT-SENDER] Saving conversation metadata with retry...');
          const conversationSaved = await saveConversationWithRetry(currentThreadId, messagesToSave);
          console.log('💾 [CHAT-SENDER] Conversation metadata save result:', conversationSaved);

          // Then save the messages
          console.log('💾 [CHAT-SENDER] Saving messages...');
          const messagesSaved = await ConversationMessageService.saveMessages(currentThreadId, messagesToSave);
          console.log('💾 [CHAT-SENDER] Messages save result:', messagesSaved);
          
          
          if (messagesSaved && conversationSaved) {
            console.log('✅ [CHAT-SENDER] Complete conversation save successful');
            toast({
              title: "Samtale lagret",
              description: "Samtalen er lagret og vil vises under 'Tidligere samtaler'.",
            });
          } else {
            console.warn('⚠️ [CHAT-SENDER] Partial save failure - messages:', messagesSaved, 'conversation:', conversationSaved);
            toast({
              title: "Delvis lagringsfeil",
              description: "Samtalen ble delvis lagret. Den kan være utilgjengelig senere.",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error('❌ [CHAT-SENDER] Complete conversation save failed:', error);
          
          // Show user-friendly error message
          toast({
            title: "Lagringsfeil",
            description: "Samtalen ble ikke lagret korrekt. Den kan gå tapt ved oppdatering.",
            variant: "destructive",
          });
        }
      } else {
        console.warn('⚠️ [CHAT-SENDER] No thread ID available for saving messages');
        
        // Show warning to user
        toast({
          title: "Manglende samtale-ID", 
          description: "Ingen samtale-ID mottatt. Samtalen lagres ikke.",
          variant: "destructive",
        });
      }
      
      console.log('🚀 [CHAT-SENDER] ===== MESSAGE SEND PROCESS COMPLETED =====');
      
    } catch (error: any) {
      console.error('💥 [CHAT-SENDER] Error sending message:', error);
      setApiError(error.message || 'En feil oppstod under sending av melding');
      
      toast({
        title: "Feil ved sending av melding",
        description: error.message || "Kunne ikke sende melding",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return { sendMessage };
};
